(() => {
  const SECTION_ID = 'bm-fitment-options';
  const ROOT_SELECTOR = '[data-bm-home-vehicle-selector]';
  const cache = new Map();
  const inflight = new Map();
  const controllers = new Map();

  const qs = (root, selector) => root?.querySelector(selector) || null;
  const qsa = (root, selector) => [...(root?.querySelectorAll(selector) || [])];
  const normalizeId = (value) => String(value || '');

  const nodeData = (choice) => ({
    id: choice?.dataset.id || '',
    name: choice?.dataset.name || '',
    url: choice?.dataset.url || '',
    image: choice?.dataset.image || '',
    logo: choice?.dataset.logo || '',
    yearFrom: choice?.dataset.yearFrom || '',
    yearTo: choice?.dataset.yearTo || '',
    codes: choice?.dataset.codes || '',
  });

  const initRoot = (root) => {
    if (!root || root.dataset.bmHomeSelectorReady === 'true') return;
    root.dataset.bmHomeSelectorReady = 'true';

    const brandPanel = qs(root, '[data-selector-stage-panel="brand"]');
    const workspacePanel = qs(root, '[data-selector-stage-panel="workspace"]');
    const modelPanel = qs(root, '[data-browser-panel="model"]');
    const generationPanel = qs(root, '[data-browser-panel="generation"]');
    const modelOptions = qs(root, '[data-dynamic-options="model"]');
    const generationOptions = qs(root, '[data-dynamic-options="generation"]');
    const summaryLogo = qs(root, '[data-summary-logo]');
    const summaryImage = qs(root, '[data-summary-image]');
    const summaryFallback = qs(root, '[data-summary-fallback]');
    const summaryEyebrow = qs(root, '[data-summary-eyebrow]');
    const summaryTitle = qs(root, '[data-summary-title]');
    const summaryMeta = qs(root, '[data-summary-meta]');
    const summaryFacts = qs(root, '[data-summary-facts]');
    const summaryYears = qs(root, '[data-summary-years]');
    const summaryCodes = qs(root, '[data-summary-codes]');
    const summaryButton = qs(root, '[data-summary-button]');
    const status = qs(root, '[data-status-text]');
    const state = { brand: null, model: null, generation: null, stage: 'brand' };
    let requestVersion = 0;
    let hydrationVersion = 0;

    const api = () => window.BandenmeestersVehicle;
    const setStatus = (text) => { if (status) status.textContent = text || ''; };

    const setImage = (img, src, alt) => {
      if (!img) return;
      if (!src) {
        img.hidden = true;
        img.removeAttribute('src');
        img.alt = '';
        return;
      }
      img.src = src;
      img.alt = alt || '';
      img.hidden = false;
    };

    const yearLabel = (item) => {
      if (!item?.yearFrom) return '';
      return `${item.yearFrom}–${item.yearTo || 'heden'}`;
    };

    const setChoiceSelection = (type, id) => {
      qsa(root, `.bm-choice[data-choice="${type}"]`).forEach((choice) => {
        const selected = normalizeId(choice.dataset.id) === normalizeId(id) && Boolean(id);
        choice.classList.toggle('is-selected', selected);
        choice.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    };

    const resetSearch = (stage) => {
      const input = qs(root, `[data-stage-search="${stage}"]`);
      if (input) input.value = '';
    };

    const clearOptions = (stage, message) => {
      const container = stage === 'model' ? modelOptions : generationOptions;
      if (!container) return;
      container.innerHTML = `<div class="bm-fitment__placeholder">${message}</div>`;
    };

    const setSummary = (stage) => {
      const brand = state.brand;
      const model = state.model;
      const generation = state.generation;
      const showFallback = (show) => { if (summaryFallback) summaryFallback.hidden = !show; };
      const setFacts = (years = '', codes = '') => {
        if (summaryYears) { summaryYears.textContent = years; summaryYears.hidden = !years; }
        if (summaryCodes) { summaryCodes.textContent = codes; summaryCodes.hidden = !codes; }
        if (summaryFacts) summaryFacts.hidden = !years && !codes;
      };

      if (summaryButton) {
        summaryButton.hidden = true;
        summaryButton.setAttribute('aria-disabled', 'true');
        summaryButton.setAttribute('href', '#');
      }

      if (stage === 'brand' || !brand) {
        if (summaryEyebrow) summaryEyebrow.textContent = 'Voertuig kiezen';
        if (summaryTitle) summaryTitle.textContent = 'Kies eerst je automerk';
        if (summaryMeta) summaryMeta.textContent = 'Kies daarna model en generatie om de gekoppelde voorraad te openen.';
        setImage(summaryLogo, '', '');
        setImage(summaryImage, '', '');
        setFacts();
        showFallback(true);
        return;
      }

      if (stage === 'model' || !model) {
        if (summaryEyebrow) summaryEyebrow.textContent = 'Merk gekozen';
        if (summaryTitle) summaryTitle.textContent = brand.name;
        if (summaryMeta) summaryMeta.textContent = 'Kies nu het model van je auto.';
        setImage(summaryLogo, brand.logo, brand.name);
        setImage(summaryImage, '', '');
        setFacts();
        showFallback(!brand.logo);
        return;
      }

      if (stage === 'generation' || !generation) {
        if (summaryEyebrow) summaryEyebrow.textContent = 'Model gekozen';
        if (summaryTitle) summaryTitle.textContent = `${brand.name} ${model.name}`.trim();
        if (summaryMeta) summaryMeta.textContent = 'Kies de juiste generatie op basis van bouwjaar en modelcode.';
        setImage(summaryLogo, '', '');
        setImage(summaryImage, model.image, `${brand.name} ${model.name}`.trim());
        setFacts();
        showFallback(!model.image);
        return;
      }

      if (summaryEyebrow) summaryEyebrow.textContent = 'Jouw auto';
      if (summaryTitle) summaryTitle.textContent = `${brand.name} ${model.name}`.trim();
      if (summaryMeta) summaryMeta.textContent = generation.name;
      setImage(summaryLogo, '', '');
      setImage(summaryImage, generation.image || model.image, `${brand.name} ${generation.name}`.trim());
      setFacts(yearLabel(generation), generation.codes);
      showFallback(!(generation.image || model.image));
      if (summaryButton && generation.url) {
        summaryButton.href = generation.url;
        summaryButton.hidden = false;
        summaryButton.removeAttribute('aria-disabled');
      }
    };

    const renderJourney = () => {
      const order = ['brand', 'model', 'generation', 'complete'];
      const currentIndex = order.indexOf(state.stage);
      qsa(root, '[data-selector-progress]').forEach((step) => {
        const stage = step.dataset.selectorProgress;
        const index = order.indexOf(stage);
        const available = stage === 'brand'
          || (stage === 'model' && Boolean(state.brand))
          || (stage === 'generation' && Boolean(state.model))
          || (stage === 'complete' && Boolean(state.generation));
        const active = stage === state.stage;
        const complete = index >= 0 && index < currentIndex;
        step.classList.toggle('is-active', active);
        step.classList.toggle('is-done', complete);
        step.classList.toggle('is-available', available || complete);
        step.setAttribute('aria-disabled', available || complete ? 'false' : 'true');
        if (available || complete) step.removeAttribute('tabindex');
        else step.tabIndex = -1;
        if (active) step.setAttribute('aria-current', 'step');
        else step.removeAttribute('aria-current');
      });

      const brandValue = qs(root, '[data-step-value="brand"]');
      const modelValue = qs(root, '[data-step-value="model"]');
      const generationValue = qs(root, '[data-step-value="generation"]');
      const completeValue = qs(root, '[data-step-value="complete"]');
      if (brandValue) brandValue.textContent = state.brand?.name || 'Kies merk';
      if (modelValue) modelValue.textContent = state.model?.name || 'Kies model';
      if (generationValue) generationValue.textContent = state.generation?.name || 'Kies generatie';
      if (completeValue) completeValue.textContent = 'Jouw auto';
    };

    const setStage = (stage) => {
      state.stage = stage;
      root.dataset.selectorStage = stage;
      if (brandPanel) brandPanel.hidden = stage !== 'brand';
      if (workspacePanel) workspacePanel.hidden = stage === 'brand';
      if (modelPanel) modelPanel.hidden = stage !== 'model';
      if (generationPanel) generationPanel.hidden = stage !== 'generation';
      renderJourney();
      setSummary(stage);
    };

    const sortChoices = (container) => {
      qsa(container, '.bm-choice[data-choice]').sort((a, b) =>
        (a.dataset.name || '').localeCompare(b.dataset.name || '', 'nl', { numeric: true, sensitivity: 'base' })
      ).forEach((choice) => container.appendChild(choice));
    };

    const fetchOptions = async (url, type) => {
      if (!url) return '';
      const key = `${type}:${url}`;
      if (cache.has(key)) return cache.get(key);
      if (inflight.has(key)) return inflight.get(key);

      const request = (async () => {
        const endpoint = new URL(url, window.location.origin);
        endpoint.searchParams.set('section_id', SECTION_ID);
        const response = await fetch(endpoint.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = doc.querySelector(`[data-bm-options="${type}"]`);
        if (!source) throw new Error(`${type} opties niet gevonden`);
        const html = source.innerHTML;
        cache.set(key, html);
        return html;
      })();

      inflight.set(key, request);
      try { return await request; }
      finally { inflight.delete(key); }
    };

    const loadStageOptions = async (url, type, expectedId) => {
      const container = type === 'model' ? modelOptions : generationOptions;
      if (!container) return false;
      const version = ++requestVersion;
      container.innerHTML = '<div class="bm-fitment__loading">Laden...</div>';
      try {
        const html = await fetchOptions(url, type);
        const stillCurrent = type === 'model'
          ? normalizeId(state.brand?.id) === normalizeId(expectedId)
          : normalizeId(state.model?.id) === normalizeId(expectedId);
        if (version !== requestVersion || !stillCurrent) return false;
        container.innerHTML = html;
        sortChoices(container);
        const choices = qsa(container, `.bm-choice[data-choice="${type}"]`);
        if (!choices.length) {
          container.innerHTML = `<div class="bm-fitment__empty">Geen ${type === 'model' ? 'modellen' : 'generaties'} gevonden.</div>`;
          return false;
        }
        return true;
      } catch (error) {
        if (version !== requestVersion) return false;
        console.error('Bandenmeesters voertuigassistent:', error);
        container.innerHTML = '<div class="bm-fitment__empty">Kon de voertuiggegevens niet laden. Probeer opnieuw.</div>';
        return false;
      }
    };

    const findChoice = (type, id) => {
      const targetId = normalizeId(id);
      if (!targetId) return null;
      return qsa(root, `.bm-choice[data-choice="${type}"]`).find((choice) =>
        normalizeId(choice.dataset.id) === targetId
      ) || null;
    };

    const resetView = () => {
      requestVersion++;
      state.brand = null;
      state.model = null;
      state.generation = null;
      setChoiceSelection('brand', '');
      setChoiceSelection('model', '');
      setChoiceSelection('generation', '');
      clearOptions('model', 'Kies eerst een merk.');
      clearOptions('generation', 'Kies eerst een model.');
      resetSearch('model');
      resetSearch('generation');
      setStage('brand');
      setStatus('Kies je automerk');
    };

    const syncVehicle = async (vehicle = api()?.getActive?.() || null) => {
      const generationId = normalizeId(vehicle?.generation?.id);
      if (!generationId) {
        hydrationVersion++;
        if (state.brand || state.model || state.generation || state.stage !== 'brand') resetView();
        return;
      }

      if (normalizeId(state.generation?.id) === generationId) {
        if (state.stage !== 'complete') setStage('complete');
        return;
      }

      const brandId = normalizeId(vehicle?.brand?.id);
      const modelId = normalizeId(vehicle?.model?.id);
      if (!brandId || !modelId) return;

      const version = ++hydrationVersion;
      const brandChoice = findChoice('brand', brandId);
      if (!brandChoice) {
        setStatus('De opgeslagen auto is niet beschikbaar in deze voertuigselectie.');
        return;
      }

      requestVersion++;
      state.brand = nodeData(brandChoice);
      state.model = null;
      state.generation = null;
      setChoiceSelection('brand', state.brand.id);
      clearOptions('generation', 'Kies eerst een model.');
      resetSearch('model');
      resetSearch('generation');
      setStage('model');
      setStatus(`Modellen van ${state.brand.name} laden...`);

      const modelsLoaded = await loadStageOptions(state.brand.url, 'model', state.brand.id);
      if (version !== hydrationVersion || !modelsLoaded) return;
      const modelChoice = findChoice('model', modelId);
      if (!modelChoice) {
        setStatus(`Het opgeslagen model is niet beschikbaar voor ${state.brand.name}.`);
        return;
      }

      state.model = nodeData(modelChoice);
      state.generation = null;
      setChoiceSelection('model', state.model.id);
      clearOptions('generation', 'Generaties laden...');
      resetSearch('generation');
      setStage('generation');
      setStatus(`Generaties van ${state.model.name} laden...`);

      const generationsLoaded = await loadStageOptions(state.model.url, 'generation', state.model.id);
      if (version !== hydrationVersion || !generationsLoaded) return;
      const generationChoice = findChoice('generation', generationId);
      if (!generationChoice) {
        setStatus(`De opgeslagen generatie is niet beschikbaar voor ${state.model.name}.`);
        return;
      }

      state.generation = nodeData(generationChoice);
      setChoiceSelection('generation', state.generation.id);
      setStage('complete');
      setStatus(`${state.generation.name} geselecteerd`);
    };

    const chooseBrand = async (choice) => {
      hydrationVersion++;
      requestVersion++;
      state.brand = nodeData(choice);
      state.model = null;
      state.generation = null;
      setChoiceSelection('brand', state.brand.id);
      clearOptions('generation', 'Kies eerst een model.');
      resetSearch('model');
      resetSearch('generation');
      setStage('model');
      setStatus(`Modellen van ${state.brand.name} laden...`);
      const ok = await loadStageOptions(state.brand.url, 'model', state.brand.id);
      setStatus(ok ? `Kies je ${state.brand.name}-model` : `Geen modellen gevonden voor ${state.brand.name}`);
    };

    const chooseModel = async (choice) => {
      hydrationVersion++;
      requestVersion++;
      state.model = nodeData(choice);
      state.generation = null;
      setChoiceSelection('model', state.model.id);
      clearOptions('generation', 'Generaties laden...');
      resetSearch('generation');
      setStage('generation');
      setStatus(`Generaties van ${state.model.name} laden...`);
      const ok = await loadStageOptions(state.model.url, 'generation', state.model.id);
      setStatus(ok ? 'Kies je generatie' : `Geen generaties gevonden voor ${state.model.name}`);
    };

    const chooseGeneration = (choice) => {
      hydrationVersion++;
      state.generation = nodeData(choice);
      setChoiceSelection('generation', state.generation.id);
      const previous = api()?.getActive?.() || null;
      const providerIds = normalizeId(previous?.generation?.id) === normalizeId(state.generation.id)
        ? (previous?.providerIds || {})
        : {};
      const vehicle = {
        source: 'internal-generation',
        brand: {
          id: state.brand?.id || '',
          name: state.brand?.name || '',
          url: state.brand?.url || '',
          logo: state.brand?.logo || '',
        },
        model: {
          id: state.model?.id || '',
          name: state.model?.name || '',
          url: state.model?.url || '',
          image: state.model?.image || '',
        },
        generation: {
          id: state.generation.id,
          name: state.generation.name,
          url: state.generation.url,
          image: state.generation.image,
          yearFrom: state.generation.yearFrom,
          yearTo: state.generation.yearTo,
          codes: state.generation.codes,
        },
        providerIds,
        selectedAt: Date.now(),
      };
      const saved = api()?.setActive?.(vehicle);
      setStage('complete');
      setStatus(saved ? `${state.generation.name} geselecteerd` : 'Auto geselecteerd; opslag kon niet worden bevestigd');
      root.dispatchEvent(new CustomEvent('bm:vehicle:selected', {
        bubbles: true,
        detail: { generation: state.generation.id, label: state.generation.name, url: state.generation.url },
      }));
    };

    const goToStage = (stage) => {
      hydrationVersion++;
      if (stage === 'brand') {
        resetView();
        return;
      }
      if (stage === 'model' && state.brand) {
        requestVersion++;
        state.model = null;
        state.generation = null;
        setChoiceSelection('model', '');
        clearOptions('generation', 'Kies eerst een model.');
        resetSearch('generation');
        setStage('model');
        setStatus(`Kies je ${state.brand.name}-model`);
        return;
      }
      if (stage === 'generation' && state.model) {
        requestVersion++;
        state.generation = null;
        setChoiceSelection('generation', '');
        setStage('generation');
        setStatus('Kies je generatie');
        return;
      }
      if (stage === 'complete' && state.generation) setStage('complete');
    };

    const filterOptions = (stage, query) => {
      const container = stage === 'model' ? modelOptions : generationOptions;
      if (!container) return;
      const needle = String(query || '').trim().toLocaleLowerCase('nl');
      let visible = 0;
      qsa(container, `.bm-choice[data-choice="${stage}"]`).forEach((choice) => {
        const haystack = `${choice.dataset.name || ''} ${choice.dataset.codes || ''} ${choice.dataset.yearFrom || ''} ${choice.dataset.yearTo || ''} ${choice.textContent || ''}`.toLocaleLowerCase('nl');
        const match = !needle || haystack.includes(needle);
        choice.hidden = !match;
        if (match) visible++;
      });
      let empty = qs(container, '[data-bm-filter-empty]');
      if (!empty) {
        empty = document.createElement('div');
        empty.className = 'bm-fitment__empty';
        empty.dataset.bmFilterEmpty = '';
        container.appendChild(empty);
      }
      empty.textContent = `Geen ${stage === 'model' ? 'modellen' : 'generaties'} gevonden.`;
      empty.hidden = visible !== 0;
    };

    root.addEventListener('click', (event) => {
      const progress = event.target.closest('[data-selector-progress]');
      if (progress && root.contains(progress)) {
        const stage = progress.dataset.selectorProgress;
        const disabled = progress.getAttribute('aria-disabled') === 'true';
        if (!disabled) goToStage(stage);
        return;
      }

      const choice = event.target.closest('.bm-choice[data-choice]');
      if (!choice || !root.contains(choice)) return;
      if (choice.dataset.choice === 'brand') chooseBrand(choice);
      else if (choice.dataset.choice === 'model') chooseModel(choice);
      else if (choice.dataset.choice === 'generation') chooseGeneration(choice);
    });

    root.addEventListener('input', (event) => {
      const input = event.target.closest('[data-stage-search]');
      if (!input || !root.contains(input)) return;
      filterOptions(input.dataset.stageSearch, input.value);
    });

    const prefetchChoice = (event) => {
      const choice = event.target.closest('.bm-choice[data-choice]');
      if (!choice || !root.contains(choice)) return;
      if (choice.dataset.choice === 'brand') fetchOptions(choice.dataset.url, 'model').catch(() => {});
      if (choice.dataset.choice === 'model') fetchOptions(choice.dataset.url, 'generation').catch(() => {});
    };
    root.addEventListener('pointerover', prefetchChoice, { passive: true });
    root.addEventListener('focusin', prefetchChoice);

    clearOptions('model', 'Kies eerst een merk.');
    clearOptions('generation', 'Kies eerst een model.');
    setChoiceSelection('brand', '');
    setStage('brand');
    setStatus('Kies je automerk');

    controllers.set(root, syncVehicle);
    syncVehicle().catch((error) => console.error('Bandenmeesters voertuigassistent:', error));
  };

  const syncControllers = (vehicle) => {
    controllers.forEach((syncVehicle, root) => {
      if (!document.documentElement.contains(root)) {
        controllers.delete(root);
        return;
      }
      syncVehicle(vehicle).catch((error) => console.error('Bandenmeesters voertuigassistent:', error));
    });
  };

  const init = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) initRoot(scope);
    qsa(scope, ROOT_SELECTOR).forEach(initRoot);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('bm:vehicle:ready', (event) => {
    syncControllers(event.detail?.vehicle ?? window.BandenmeestersVehicle?.getActive?.() ?? null);
  });
  window.addEventListener('bm:vehicle:change', (event) => {
    syncControllers(event.detail?.vehicle ?? window.BandenmeestersVehicle?.getActive?.() ?? null);
  });
  window.addEventListener('pageshow', () => {
    syncControllers(window.BandenmeestersVehicle?.getActive?.() || null);
  });
})();
